/**
 * Stable, token-free URLs for PUBLIC Supabase-hosted media.
 *
 * Cached payloads (unstable_cache, full route cache, router prefetch) must
 * never embed expiring signed URLs — an entry older than the token lifetime
 * serves covers that all fail (ERR_BLOCKED_BY_ORB → initials fallback;
 * production incident 2026-06-12 on /dancers). Instead the markup carries
 * `/api/media/cover/<variant>/<base64url(path)>` and the route handler mints
 * the signed URL at image-request time. See
 * docs/wip/active/WIP_implementing_stable_media_urls_2026_06_12.md.
 *
 * PRIVATE surfaces (chat, admin tools, contracts) must NOT use this — they
 * keep per-request signing via the profile-photo-url.ts helpers.
 */

export const COVER_VARIANTS = {
  /** Card grids — matches the R2 GALLERY variant (800px, q80).
   * resize:'contain' is REQUIRED with a single dimension: Supabase's
   * default cover does NOT scale proportionally — it serves a
   * width-cropped FULL-HEIGHT strip (4016×6016 → 800×6016; dancer
   * directory scalp-zoom regression 2026-06-12, see 220c3e5cf7). */
  gallery: { width: 800, quality: 80, resize: 'contain' },
  /** Full-size — detail-page galleries / lightbox. */
  original: null,
} as const;

export type CoverVariant = keyof typeof COVER_VARIANTS;

export function isCoverVariant(value: string): value is CoverVariant {
  return Object.prototype.hasOwnProperty.call(COVER_VARIANTS, value);
}

// base64url via base64 + charset translation, NOT Buffer's 'base64url'
// encoding: this module is imported by client components (search results,
// entity cards), and the browser Buffer polyfill (feross/buffer) only
// supports 'base64' — 'base64url' throws "Unknown encoding" at runtime.
export function encodeStoragePath(path: string): string {
  return Buffer.from(path, 'utf8')
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Decode a base64url segment back to a storage path. Returns null for
 * anything that is not strictly base64url — the route treats null as 400.
 */
export function decodeStoragePath(encoded: string): string | null {
  if (!/^[A-Za-z0-9_-]+$/.test(encoded)) return null;
  const base64 =
    encoded.replace(/-/g, '+').replace(/_/g, '/') +
    '='.repeat((4 - (encoded.length % 4)) % 4);
  try {
    return Buffer.from(base64, 'base64').toString('utf8');
  } catch {
    return null;
  }
}

/**
 * Map a DANCER_MEDIA bucket-relative storage path to its stable proxy URL.
 *
 * Full http(s) URLs (Meteor S3 legacy `external_url` values) pass through
 * unchanged; null/empty stays null so EntityCard renders its placeholder.
 */
export function publicCoverUrl(
  storagePath: string | null | undefined,
  variant: CoverVariant,
): string | null {
  if (!storagePath || storagePath.trim() === '') return null;
  if (storagePath.startsWith('http://') || storagePath.startsWith('https://')) {
    return storagePath;
  }
  return `/api/media/cover/${variant}/${encodeStoragePath(storagePath)}`;
}
